import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Option } from '../../../../../shared/models/option.model';
import { InputConfig } from '../../../../../shared/ui/form/input/input.component.config';
import { SelectConfig } from '../../../../../shared/ui/form/select/select.component.config';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SubmittedErrorStateMatcher } from '../../../../shared/utils/submitted-error-state.matcher';
import { legacyErrorMessage } from '../../../../shared/utils/legacy-api-response.util';
import { trimmedRequiredValidator } from '../../../../shared/utils/bank-validators.util';
import { PaymentCardTypes } from '../../../../../modules/finanzas/paymentcard/paymentcard.model';
import { PaymentCardCrudService } from '../../services/payment-card-crud.service';
import {
  formatPaymentCardExpiry,
  maskPaymentCardNumber,
  parsePaymentCardExpiry,
  PaymentCardEntity,
  PaymentCardFormDialogData,
  PaymentCardPayload,
} from '../../models/payment-card-form.model';
import { PaymentCardFormLookupsFacade } from './payment-card-form-lookups.facade';

type PaymentCardControl = 'name' | 'number' | 'expiring' | 'bankAccount';

function expiryValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value || '').trim();
  if (!value) return null;
  return parsePaymentCardExpiry(value) ? null : { expiry: true };
}

function optionRequiredValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return value && typeof value === 'object' && value.id ? null : { option: true };
}

@Component({
  selector: 'app-payment-card-form-dialog',
  templateUrl: './payment-card-form-dialog.component.html',
  styleUrls: ['./payment-card-form-dialog.component.scss'],
  standalone: false,
  providers: [
    SubmittedErrorStateMatcher,
    { provide: ErrorStateMatcher, useExisting: SubmittedErrorStateMatcher },
  ],
})
export class PaymentCardFormDialogComponent implements OnInit {
  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    number: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    expiring: new FormControl('', { nonNullable: true, validators: [expiryValidator] }),
    bankAccount: new FormControl<Option | string | null>(null, { validators: [optionRequiredValidator] }),
  });

  entity: PaymentCardEntity | null = null;
  bankAccounts: Option[] = [];
  bankAccountConfig: SelectConfig = this.buildBankAccountConfig();

  readonly nameConfig: InputConfig = {
    label: 'Nombre',
    maxLength: 255,
    errors: { required: 'Indica un nombre para la tarjeta' },
  };

  readonly numberConfig: InputConfig = {
    label: 'Número de tarjeta',
    maxLength: 255,
    suffixIcon: 'credit_card',
    errors: { required: 'Indica el número de tarjeta' },
  };

  readonly expiringConfig: InputConfig = {
    label: 'Caducidad (MM/AA)',
    placeholder: '05/28',
    maxLength: 7,
    errors: { expiry: 'Usa el formato MM/AA' },
  };

  ready = false;
  loadError = '';
  saving = false;
  submitted = false;
  saveError = '';
  showUid = false;

  static open(dialog: MatDialog, data: PaymentCardFormDialogData): Observable<boolean> {
    return dialog.open<PaymentCardFormDialogComponent, PaymentCardFormDialogData, boolean>(PaymentCardFormDialogComponent, {
      width: '580px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<PaymentCardFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentCardFormDialogData,
    private crud: PaymentCardCrudService,
    private lookups: PaymentCardFormLookupsFacade,
    private notify: NotificationService,
    private errorState: SubmittedErrorStateMatcher,
  ) { }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get isCreditCard(): boolean {
    return this.data.paymentCardType === PaymentCardTypes.Tarjeta_Credito;
  }

  get title(): string {
    const kind = this.isCreditCard
      ? 'tarjeta de crédito'
      : this.data.paymentCardType === PaymentCardTypes.Tarjeta_Debito ? 'tarjeta de débito' : 'tarjeta';
    return `${this.isEdit ? 'Editar' : 'Nueva'} ${kind}`;
  }

  get subtitle(): string {
    if (this.isCreditCard && this.isEdit && this.entity && this.entity.accountNumber) return 'Cuenta contable ' + this.entity.accountNumber;
    if (this.isCreditCard && !this.isEdit) return 'Se creará su cuenta contable 5200 automáticamente';
    return 'Datos de la tarjeta y cuenta bancaria asociada';
  }

  hasError(control: PaymentCardControl, error: string): boolean {
    return this.submitted && this.form.controls[control].hasError(error);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.ready = false;
    this.loadError = '';

    const initial = this.data.paymentCard;
    const entityRequest: Promise<PaymentCardEntity | null> = !this.isEdit
      ? Promise.resolve(null)
      : initial && initial.uid === this.data.uid
        ? Promise.resolve(initial)
        : this.crud.get(this.data.uid as string);

    Promise.all([this.lookups.bankAccounts(), entityRequest])
      .then(([accounts, entity]) => {
        this.data.paymentCard = null;
        this.bankAccounts = accounts || [];
        this.bankAccountConfig = this.buildBankAccountConfig();
        this.applyEntity(entity);
      })
      .catch(error => { this.loadError = legacyErrorMessage(error, 'No se pudo cargar la tarjeta.'); })
      .then(() => { this.ready = true; });
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.errorState.submitted = true;
    this.saveError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const payload = this.buildPayload();
    const uid = this.isEdit ? payload.uid || (this.data.uid as string) : null;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const request = uid ? this.crud.update(uid, payload) : this.crud.create(payload);
    request
      .then(() => {
        this.notify.ok(uid ? 'Tarjeta actualizada correctamente' : 'Tarjeta creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = legacyErrorMessage(error, uid ? 'No se pudo actualizar la tarjeta' : 'No se pudo crear la tarjeta');
      });
  }

  cancel(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private buildBankAccountConfig(): SelectConfig {
    return {
      label: 'Cuenta bancaria',
      placeholder: 'Seleccionar cuenta…',
      options: this.bankAccounts,
      autoSelectSingle: true,
      hint: this.bankAccounts.length ? '' : 'No tienes cuentas bancarias. Crea una en Finanzas › Cuentas bancarias.',
      errors: { option: 'Selecciona la cuenta bancaria' },
    };
  }

  private applyEntity(source: PaymentCardEntity | null): void {
    this.entity = source ? {
      uid: source.uid,
      bankAccountUid: source.bankAccountUid,
      type: source.type,
      name: source.name || '',
      number: source.number || '',
      monthExpiring: Number(source.monthExpiring) || 0,
      yearExpiring: Number(source.yearExpiring) || 0,
      accountNumber: source.accountNumber || '',
      projectUid: source.projectUid,
      expenseProjectTypeUid: source.expenseProjectTypeUid,
    } : null;

    const bankAccountUid = this.entity ? String(this.entity.bankAccountUid || '').toLowerCase() : '';
    const account = bankAccountUid ? this.bankAccounts.find(option => String(option.id).toLowerCase() === bankAccountUid) : null;

    this.form.reset({
      name: this.entity ? this.entity.name : '',
      number: this.entity ? this.entity.number : '',
      expiring: this.entity ? formatPaymentCardExpiry(this.entity.monthExpiring, this.entity.yearExpiring) : '',
      bankAccount: account || null,
    });
  }

  private buildPayload(): PaymentCardPayload {
    const value = this.form.getRawValue();
    const base = this.entity;
    const expiry = parsePaymentCardExpiry(value.expiring);
    const account = value.bankAccount as Option;
    const payload: PaymentCardPayload = {
      bankAccountUid: String(account.id),
      type: this.data.paymentCardType,
      name: value.name.trim(),
      number: maskPaymentCardNumber(value.number.trim()),
      monthExpiring: expiry ? expiry.month : 0,
      yearExpiring: expiry ? expiry.year : 0,
    };
    if (base) {
      if (base.uid) payload.uid = base.uid;
      if (base.accountNumber) payload.accountNumber = base.accountNumber;
      if (base.projectUid) payload.projectUid = base.projectUid;
      if (base.expenseProjectTypeUid) payload.expenseProjectTypeUid = base.expenseProjectTypeUid;
    }
    return payload;
  }
}
